/**
 * Bandes de couleur d'un score de complétude (0-100).
 *
 * Ces trois fonctions vivaient dans
 * `pages/apps/properties/property-listing-quality/property-listing-quality.helpers.ts`.
 * Elles sont remontées ici parce que `<app-completion-score-card>` (sous
 * `shared/components/`) en a besoin, et qu'un fichier de `shared/` ne peut pas
 * importer depuis `pages/` — c'est l'inversion de couche que
 * `scripts/check-layer-boundaries.js` interdit.
 *
 * Les seuils sont INCHANGÉS par rapport à l'original : ce déplacement ne doit
 * modifier aucun rendu existant. `property-listing-quality.helpers.ts` les
 * ré-exporte, donc tous les imports et specs d'origine continuent de marcher.
 *
 * Pur TS — ni Angular ni Firestore.
 */

/** Niveau qualitatif d'un score, partagé par le scoring bien et la complétude workspace. */
export type ScoreLevel = 'excellent' | 'good' | 'fair' | 'poor';

/**
 * Tailwind text colour for the overall quality percentage. Bands:
 * - ≥85 % → green-600
 * - ≥70 % → blue-600
 * - ≥50 % → orange-600
 * - else  → red-600
 */
export const qualityColorClass = (percentage: number | undefined): string => {
  if (percentage === undefined) return '';
  if (percentage >= 85) return 'text-green-600';
  if (percentage >= 70) return 'text-blue-600';
  if (percentage >= 50) return 'text-orange-600';
  return 'text-red-600';
};

/**
 * Material progress-bar `color` for the overall quality percentage.
 * primary (≥85) / accent (≥70) / warn (else).
 */
export const progressColor = (percentage: number | undefined): 'primary' | 'accent' | 'warn' => {
  if (percentage === undefined) return 'warn';
  if (percentage >= 85) return 'primary';
  if (percentage >= 70) return 'accent';
  return 'warn';
};

/** Tailwind chip classes for the impact badge. */
export const impactColorClass = (impact: string): string => {
  switch (impact) {
    case 'high':   return 'bg-red-100 text-red-700';
    case 'medium': return 'bg-orange-100 text-orange-700';
    case 'low':    return 'bg-blue-100 text-blue-700';
    default:       return 'bg-gray-100 text-gray-700';
  }
};

/**
 * Niveau qualitatif d'un pourcentage, avec les MÊMES seuils que
 * `getQualityLevel()` de `core/services/property-analysis/property-scoring.helpers.ts`
 * (≥85 excellent · ≥70 good · ≥50 fair · sinon poor).
 *
 * Dupliqué ici volontairement plutôt qu'importé : `property-scoring.helpers.ts`
 * vit sous `core/` et tire le modèle `Property`, dont la complétude workspace
 * n'a nul besoin. Les deux jeux de seuils sont verrouillés ensemble par
 * `score-bands.spec.ts`.
 */
export const scoreLevel = (percentage: number): ScoreLevel => {
  if (percentage >= 85) return 'excellent';
  if (percentage >= 70) return 'good';
  if (percentage >= 50) return 'fair';
  return 'poor';
};
